import * as React from 'react';

import { PaginationWrapper, PaginationItem } from '../../components/Pagination';
import { usePagination } from './utils';
import { getTotalCountPages, getCurrentPage } from '../../utils/pagination';
import { PAGINATION_WIDTH } from '../../constants/pagination';

export interface IPagination {
  offset: number;
  limit: number;
  total: number;
}

export interface IPaginationContainerProps {
  paginationWidth?: number;
  disabled?: boolean;
  pagination: IPagination;
  action(pageSelected: number): void;
  scrollToTop?: () => void;
}

export const PaginationContainer: React.FC<React.PropsWithChildren<IPaginationContainerProps>> = ({
  paginationWidth = PAGINATION_WIDTH,
  disabled,
  pagination,
  action,
  scrollToTop,
}) => {
  const { offset, limit, total } = pagination;

  const pagesTotal = getTotalCountPages(total, limit);
  const pageActive = getCurrentPage(offset, limit);

  const onSelect = React.useCallback(
    (pageSelected: number) => {
      action(pageSelected);
      if (scrollToTop) {
        scrollToTop();
      }
    },
    [action, scrollToTop],
  );

  const { getText, handleNext, handlePrev, pagesList } = usePagination({
    pagesTotal,
    pageActive,
    paginationWidth,
    onSelect,
  });

  if (pagesTotal === 1) {
    return null;
  }

  return (
    <PaginationWrapper
      onNext={handleNext}
      onPrev={handlePrev}
      disabled={disabled}
      isFirstPage={pageActive === 1}
      isLastPage={pageActive === pagesTotal}
    >
      {pagesList.map((pageNumber, itemIndex) => {
        const text = getText(pageNumber, itemIndex);
        const isActive = pageNumber === pageActive;
        const onClickHandler = isActive ? undefined : () => onSelect(pageNumber);

        return (
          <PaginationItem key={pageNumber} isActive={pageNumber === pageActive} onClick={onClickHandler}>
            {text}
          </PaginationItem>
        );
      })}
    </PaginationWrapper>
  );
};

PaginationContainer.displayName = 'PaginationContainer';
